import {
  Box,
  Button,
  ContentLayout,
  ExpandableSection,
  Header,
  ProgressBar,
  Table,
} from '@cloudscape-design/components';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { createItem, deleteItem, fetchItem, updateItem } from '../api/generic';
import { BatchChooserModal } from '../components/BatchChooserModal';
import DisplayImage from '../components/DisplayImage';
import Flex from '../components/Flex';
import { useAuthContext } from '../contexts/AuthContext';
import { Batch } from '../models/Batch';
import { type Brew } from '../models/Brew';
import { RecipeIngredient } from '../models/Inventory';
import { formatNumber } from '../utils/number';
import { prefixCollection } from '../utils/prefix';
import { getCurrentDayInRecipe } from '../utils/recipe';

type MergedIngredient = {
  key: string; // stable key (itemId+unit or name+unit)
  name: string;
  total: number;
  unit?: string;
  itemId?: string;
  fromBatches: string[]; // for traceability
};

export function mergeIngredientsFromBatches(batches: Batch[]): MergedIngredient[] {
  const map = new Map<string, MergedIngredient>();

  for (const batch of batches.filter((b) => !b.ingredientsConfirmed)) {
    const ings = batch.recipe?.ingredients ?? [];
    // scale e.g. if recipe is for 100L but batch.volume is 160L
    const scale = batch.recipe?.batchSize && batch.volume ? batch.volume / batch.recipe.batchSize : 1;

    for (const ing of ings as RecipeIngredient[]) {
      const itemId: string | undefined = ing.item.id;
      const name: string = ing.item.name;
      const unit: string | undefined = ing.item.unit;

      const qty: number = (ing.quantity ?? 0) * scale;

      // Keep unit in the key to avoid mixing different units
      const key = `${itemId ?? name}::${unit ?? ''}`;

      const prev = map.get(key);
      if (prev) {
        prev.total += qty;
        prev.fromBatches.push(batch?.batchId ?? '');
      } else {
        map.set(key, {
          key,
          name,
          unit,
          total: qty,
          itemId,
          fromBatches: [batch?.batchId ?? ''],
        });
      }
    }
  }

  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

const BrewView = () => {
  const { brewId } = useParams<{ brewId?: string }>();
  const navigate = useNavigate();
  const { tenant } = useAuthContext();

  const [brew, setBrew] = useState<Brew | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [showChooser, setShowChooser] = useState<boolean>(!brewId);

  const [progressOpen, setProgressOpen] = useLocalStorage<boolean>('brew-progress-open', true);
  const [ingredientsOpen, setIngredientsOpen] = useLocalStorage<boolean>('brew-ingredients-open', true);

  const goToNextStep = async (batch: Batch) => {
    if (!batch.recipe) return;
    const lastIndex = batch.recipe.steps.length - 1;
    const nextIndex = Math.min(batch.currentStepIndex + 1, lastIndex);

    // if we're already at the last step, do nothing (or mark completed)
    if (nextIndex === batch.currentStepIndex) return;

    // optional: mark previous step end time and next step start time if you track them
    const prevStepKey = `recipe.steps.${batch.currentStepIndex}.end`;
    const nextStepKey = `recipe.steps.${nextIndex}.start`;

    // Persist
    await updateItem<Batch>(prefixCollection('batches', tenant), batch.id, {
      currentStepIndex: nextIndex,
      stepStatus: 'in-progress' as any, // keep your existing StepStaus type
      [prevStepKey]: new Date(),
      [nextStepKey]: new Date(),
    });

    // Optimistic UI update
    setBatches((bs) =>
      bs.map((b) =>
        b.id === batch.id
          ? {
              ...b,
              currentStepIndex: nextIndex,
              stepStatus: 'in-progress' as any,
              recipe: b.recipe
                ? {
                    ...b.recipe,
                    steps: b.recipe.steps.map((s, i) =>
                      i === batch.currentStepIndex
                        ? { ...s, end: new Date() }
                        : i === nextIndex
                          ? { ...s, start: new Date() }
                          : s,
                    ),
                  }
                : b.recipe,
            }
          : b,
      ),
    );
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!brewId) {
        setShowChooser(true);
        return;
      }
      try {
        const doc = await fetchItem<Brew>(prefixCollection('brews', tenant), brewId);
        setShowChooser(false);
        if (!cancelled) {
          if (doc) {
            setBrew(doc);
          }
        }
      } catch (e: any) {
        // Handle error
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [brewId, tenant]);

  const getBatches = async () => {
    if (!brew) return;
    const fetchedBatches = await Promise.all(
      brew.batchIds.map((id) => fetchItem<Batch>(prefixCollection('batches', tenant), id)),
    );
    setBatches(fetchedBatches.filter((b) => b !== null) as Batch[]);
  };

  useEffect(() => {
    if (brew) getBatches();
  }, [brew]);

  const LEFT_COL = '15%';

  const merged = useMemo(() => mergeIngredientsFromBatches(batches), [batches]);

  const handleAbandonBrew = async () => {
    if (!brew) return;
    if (!confirm('Are you sure you want to abandon this brew? This action cannot be undone.')) return;
    await deleteItem(prefixCollection('brews', tenant), brew.id);
    navigate('/');
  };

  return (
    <ContentLayout
      header={
        <Header
          actions={
            <Button variant="primary" onClick={handleAbandonBrew}>
              Abandon this Brew
            </Button>
          }
          variant="h1"
        >
          Brew Dashboard
        </Header>
      }
    >
      {!brewId && (
        <BatchChooserModal
          visible={showChooser}
          onDismiss={() => setShowChooser(false)}
          onConfirm={async (selected) => {
            // create a new brew and navigate to it
            const payload: Omit<Brew, 'id'> = {
              name: selected.length === 1 ? `Brew: ${selected[0].name}` : `Brew (${selected.length} batches)`,
              createdAt: Date.now(),
              batchIds: selected.map((b) => b.id),
              status: 'active',
            };
            const created = await createItem(prefixCollection('brews', tenant), payload);
            navigate(`/brew/${created.id}`);
          }}
        />
      )}
      {!showChooser && brew && (
        <ExpandableSection
          headerText="Ingredients"
          expanded={ingredientsOpen}
          onChange={({ detail }) => setIngredientsOpen(detail.expanded)}
        >
          <Flex column align="stretch" style={{ gap: 2, width: '100%' }}>
            <Table
              variant="borderless"
              items={merged}
              columnDefinitions={[
                { id: 'name', header: 'Name', cell: (item) => item.name },
                { id: 'amount', header: 'Amount', cell: (item) => `${formatNumber(item.total)} ${item.unit}` },
                { id: 'fromBatches', header: 'From Batches', cell: (item) => item.fromBatches.join(', ') },
              ]}
            />
          </Flex>
        </ExpandableSection>
      )}
      {!showChooser && brew && (
        <ExpandableSection
          headerText="Progress"
          expanded={progressOpen}
          onChange={({ detail }) => setProgressOpen(detail.expanded)}
        >
          <Flex column align="stretch" style={{ gap: 2, width: '100%' }}>
            {batches.map((batch) => {
              const totalDays = batch.recipe?.steps.reduce((total, step) => total + (step.duration || 0), 0) || 1;
              const currentDay = getCurrentDayInRecipe(batch);
              const currentStep = batch.recipe?.steps[batch.currentStepIndex];
              return (
                <Flex
                  key={batch.id}
                  justify="start"
                  align="center"
                  style={{ gap: 16, width: '100%', borderBottom: '1px solid #ddd', marginBottom: 8 }}
                >
                  <div style={{ width: LEFT_COL, flex: '0 0 auto' }}>
                    <DisplayImage
                      fileName="image"
                      filePath={'recipes/' + batch.recipeId}
                      round
                      altText="Recipe image"
                      style={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                        border: 'solid 2px black',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Flex column align="start" style={{ gap: 4 }}>
                      <ProgressBar
                        value={(currentDay / totalDays) * 100}
                        description={`${batch.batchId}. Day ${currentDay} of ${totalDays} (${batch.recipe?.name})`}
                        label={
                          <Flex justify="start" align="center">
                            <Box variant="h3">{`${batch.recipe?.steps[batch.currentStepIndex]?.name} of ${batch.name}`}</Box>
                          </Flex>
                        }
                      />

                      <p>{currentStep?.description}</p>
                    </Flex>
                  </div>
                  <div style={{ width: LEFT_COL, flex: '0 0 auto' }}>
                    <Button
                      variant="primary"
                      iconName="angle-right"
                      iconAlign="right"
                      disabled={!batch.recipe || batch.currentStepIndex >= (batch.recipe?.steps.length ?? 1) - 1}
                      onClick={() => goToNextStep(batch)}
                    >
                      {batch.recipe && batch.currentStepIndex < batch.recipe.steps.length - 1
                        ? `${batch.recipe.steps[batch.currentStepIndex + 1].name}`
                        : 'All steps completed'}
                    </Button>
                  </div>
                </Flex>
              );
            })}
          </Flex>
        </ExpandableSection>
      )}
    </ContentLayout>
  );
};

export default BrewView;
